import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Trash2, Plus, BookMarked, AlertCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CCBSkillHint from "@/components/CCBSkillHint";

const BASE = process.env.REACT_APP_BACKEND_URL;

const AGENTS = [
  "compliance",
  "user-stories",
  "impact-analysis",
  "meeting-intel",
  "gherkin",
  "structuring",
  "other",
];

const CATEGORIES = [
  { value: "wrong_format",      label: "Wrong format" },
  { value: "hallucination",     label: "Hallucination" },
  { value: "missing_context",   label: "Missing context" },
  { value: "wrong_assumption",  label: "Wrong assumption" },
  { value: "incomplete",        label: "Incomplete output" },
  { value: "off_topic",         label: "Off topic" },
];

const CATEGORY_COLORS: Record<string, string> = {
  wrong_format:     "bg-orange-100 text-orange-700",
  hallucination:    "bg-red-100 text-red-700",
  missing_context:  "bg-yellow-100 text-yellow-800",
  wrong_assumption: "bg-purple-100 text-purple-700",
  incomplete:       "bg-blue-100 text-blue-700",
  off_topic:        "bg-gray-100 text-gray-600",
};

interface LogEntry {
  id: string;
  agent_name: string;
  what_went_wrong: string;
  correction: string;
  lesson: string;
  category: string | null;
  flagged_for_prompt: string | null;
  created_at: string;
}

interface Props {
  baProjectId: string;
}

const EMPTY_FORM = {
  agent_name: "compliance",
  what_went_wrong: "",
  correction: "",
  lesson: "",
  category: "",
  flagged_for_prompt: "",
};

export default function EvolutionLogPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const fetchLogs = async () => {
    if (!baProjectId) return;
    try {
      const { data } = await axios.get(
        `${BASE}/api/tools/evolution-logs?project_id=${baProjectId}`,
        { headers: authHeader }
      );
      setLogs(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [baProjectId]);

  const handleSave = async () => {
    if (!form.what_went_wrong.trim() || !form.correction.trim() || !form.lesson.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${BASE}/api/tools/evolution-logs`, {
        project_id: baProjectId,
        agent_name: form.agent_name,
        what_went_wrong: form.what_went_wrong.trim(),
        correction: form.correction.trim(),
        lesson: form.lesson.trim(),
        category: form.category || null,
        flagged_for_prompt: form.flagged_for_prompt.trim() || null,
      }, { headers: authHeader });
      setForm({ ...EMPTY_FORM });
      setShowForm(false);
      fetchLogs();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    await axios.delete(`${BASE}/api/tools/evolution-logs/${id}`, { headers: authHeader });
    setLogs((prev) => prev.filter((l) => l.id !== id));
  };

  // Derive top lessons for the summary strip
  const agentCounts = logs.reduce<Record<string, number>>((acc, l) => {
    acc[l.agent_name] = (acc[l.agent_name] || 0) + 1;
    return acc;
  }, {});
  const topAgent = Object.entries(agentCounts).sort((a, b) => b[1] - a[1])[0];

  const categoryCounts = logs.reduce<Record<string, number>>((acc, l) => {
    if (l.category) acc[l.category] = (acc[l.category] || 0) + 1;
    return acc;
  }, {});
  const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="p-5 max-w-2xl space-y-4">
    <CCBSkillHint tab="evolution-log" className="mb-4" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <BookMarked className="h-4 w-4 text-primary" />
            Evolution Log
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            When AI gets it wrong, log it here. Over time this becomes the skill that fixes itself.
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setShowForm((v) => !v)}>
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {showForm ? "Cancel" : "Log Issue"}
        </Button>
      </div>

      {/* Stats strip */}
      {logs.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-primary/8 border border-primary/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-primary">{logs.length}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Issues logged</p>
          </div>
          {topAgent && (
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-sm font-bold text-foreground truncate">{topAgent[0]}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Most flagged agent</p>
            </div>
          )}
          {topCategory && (
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-sm font-bold text-foreground">
                {CATEGORIES.find((c) => c.value === topCategory[0])?.label ?? topCategory[0]}
              </p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Top error type</p>
            </div>
          )}
        </div>
      )}

      {/* Log form */}
      {showForm && (
        <div className="bg-[#e4ecfb] rounded-lg p-4 space-y-3 border border-[#bcc8e0]">
          <p className="text-xs font-semibold text-foreground uppercase tracking-wider">New Entry</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Agent</label>
              <select
                value={form.agent_name}
                onChange={(e) => setForm({ ...form, agent_name: e.target.value })}
                className="w-full text-xs border border-[#bcc8e0] rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {AGENTS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full text-xs border border-[#bcc8e0] rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">— select —</option>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">What went wrong</label>
            <textarea
              value={form.what_went_wrong}
              onChange={(e) => setForm({ ...form, what_went_wrong: e.target.value })}
              placeholder="Describe the AI mistake specifically…"
              rows={2}
              className="w-full text-xs border border-[#bcc8e0] rounded-md px-2 py-1.5 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Correction</label>
            <textarea
              value={form.correction}
              onChange={(e) => setForm({ ...form, correction: e.target.value })}
              placeholder="What should the correct output have been?"
              rows={2}
              className="w-full text-xs border border-[#bcc8e0] rounded-md px-2 py-1.5 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Lesson — rule to apply next time</label>
            <textarea
              value={form.lesson}
              onChange={(e) => setForm({ ...form, lesson: e.target.value })}
              placeholder="e.g. Always include PHI flag check before suggesting email channel…"
              rows={2}
              className="w-full text-xs border border-[#bcc8e0] rounded-md px-2 py-1.5 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Flag for prompt update (optional)</label>
            <Input
              value={form.flagged_for_prompt}
              onChange={(e) => setForm({ ...form, flagged_for_prompt: e.target.value })}
              placeholder="e.g. compliance-system-prompt, gherkin-skill"
              className="text-xs h-7 bg-white border-[#bcc8e0]"
            />
          </div>

          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !form.what_went_wrong.trim() || !form.correction.trim() || !form.lesson.trim()}
            className="w-full"
          >
            {saving ? "Saving…" : "Save Entry"}
          </Button>
        </div>
      )}

      {/* Log list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16">
          <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No issues logged yet</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            When AI output is wrong, log it here. The lessons accumulate into a skill improvement record.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <div key={log.id} className="bg-card border border-border rounded-lg p-4 group">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {log.agent_name}
                  </span>
                  {log.category && (
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLORS[log.category] ?? "bg-muted text-muted-foreground"}`}>
                      {CATEGORIES.find((c) => c.value === log.category)?.label ?? log.category}
                    </span>
                  )}
                  {log.flagged_for_prompt && (
                    <span className="text-[10px] text-muted-foreground border border-dashed border-border px-2 py-0.5 rounded-full">
                      → {log.flagged_for_prompt}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(log.created_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => handleDelete(log.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">What went wrong</p>
                  <p className="text-xs text-foreground leading-relaxed">{log.what_went_wrong}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Correction</p>
                  <p className="text-xs text-foreground leading-relaxed">{log.correction}</p>
                </div>
                <div className="bg-primary/6 border-l-2 border-primary rounded-r-md px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-0.5">Lesson</p>
                  <p className="text-xs text-foreground leading-relaxed">{log.lesson}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
